import { useCallback, useEffect, useState } from "react";
import {
  createView,
  deleteView,
  listViews,
  setDefault as apiSetDefault,
  unsetDefault as apiUnsetDefault,
  updateView,
} from "@/api/views";
import type { SavedViewEntity, SavedViewRead } from "@/api/types";

export interface UseSavedViewsResult {
  views: SavedViewRead[];
  /** The user's default view for this entity, or null. */
  defaultView: SavedViewRead | null;
  loading: boolean;
  reload: () => Promise<void>;
  create: (
    name: string,
    filters: Record<string, unknown>,
  ) => Promise<SavedViewRead>;
  update: (
    id: number,
    data: { name?: string; filters?: Record<string, unknown> },
  ) => Promise<SavedViewRead>;
  remove: (id: number) => Promise<void>;
  setDefault: (id: number) => Promise<SavedViewRead>;
  unsetDefault: (id: number) => Promise<SavedViewRead>;
}

/**
 * Named, per-project saved views for a page, backed by the shared
 * `/projects/{slug}/views` API (discriminated by `entity`). Plain axios —
 * no React Query — with an explicit reload after each mutation.
 *
 * A view's `filters` field is a free-form JSON blob owned by the page
 * (the Tasks page stores tab / view mode / assignee / statuses / columns).
 * Reusable across pages — same hook, different `entity`.
 */
export function useSavedViews(
  entity: SavedViewEntity,
  slug: string | null,
): UseSavedViewsResult {
  const [views, setViews] = useState<SavedViewRead[]>([]);
  const [loading, setLoading] = useState<boolean>(!!slug);

  const reload = useCallback(async () => {
    if (!slug) {
      setViews([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setViews(await listViews(slug, entity));
    } catch {
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, [slug, entity]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(
    async (name: string, filters: Record<string, unknown>) => {
      if (!slug) throw new Error("No project selected");
      const view = await createView(slug, { name, entity, filters });
      await reload();
      return view;
    },
    [slug, entity, reload],
  );

  const update = useCallback(
    async (
      id: number,
      data: { name?: string; filters?: Record<string, unknown> },
    ) => {
      if (!slug) throw new Error("No project selected");
      const view = await updateView(slug, id, data);
      await reload();
      return view;
    },
    [slug, reload],
  );

  const remove = useCallback(
    async (id: number) => {
      if (!slug) return;
      await deleteView(slug, id);
      await reload();
    },
    [slug, reload],
  );

  const setDefault = useCallback(
    async (id: number) => {
      if (!slug) throw new Error("No project selected");
      const view = await apiSetDefault(slug, id);
      await reload();
      return view;
    },
    [slug, reload],
  );

  const unsetDefault = useCallback(
    async (id: number) => {
      if (!slug) throw new Error("No project selected");
      const view = await apiUnsetDefault(slug, id);
      await reload();
      return view;
    },
    [slug, reload],
  );

  const defaultView = views.find((v) => v.is_default) ?? null;

  return {
    views,
    defaultView,
    loading,
    reload,
    create,
    update,
    remove,
    setDefault,
    unsetDefault,
  };
}
